// NOTE: RFC-4180 CSV serialization. The existing `downloadCSV` only writes a
// blob; escaping and row assembly live here so every export is parser-safe.

const NEEDS_QUOTING = /[",\r\n]/;

// Cells starting with these are interpreted as formulas by Excel/Sheets.
const FORMULA_TRIGGERS = /^[=+\-@\t\r]/;
// A lone dash (the empty-value placeholder) or a plain signed number is not a
// formula-injection vector — leaving it untouched preserves on-screen parity
// (e.g. the `-` placeholder must not render as `'-`).
const SAFE_VALUE = /^-$|^[+-]?\d+(\.\d+)?$/;

/**
 * Escape a single CSV cell per RFC-4180: wrap in double quotes when the value
 * contains a comma, double-quote, or newline, doubling any embedded quote.
 */
export const escapeCsvCell = (value: string): string =>
  NEEDS_QUOTING.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/**
 * Neutralize spreadsheet formula injection (CWE-1236): a value that starts with
 * `=`, `+`, `-`, `@`, tab, or CR is prefixed with a single quote so Excel/Sheets
 * treat it as text rather than evaluating it as a formula on open.
 */
export const neutralizeFormula = (value: string): string =>
  FORMULA_TRIGGERS.test(value) && !SAFE_VALUE.test(value) ? `'${value}` : value;

/** Export-safe cell: formula-neutralized first, then RFC-4180 escaped. */
export const sanitizeCsvCell = (value: string): string =>
  escapeCsvCell(neutralizeFormula(value));

export type CsvColumn<T> = {
  header: string;
  getValue: (row: T) => string;
};

/**
 * Build an RFC-4180 CSV string from a column definition and a list of rows.
 * Rows are separated by CRLF; nullish cell values become empty strings.
 */
export const buildCsv = <T>(columns: CsvColumn<T>[], rows: T[]): string => {
  const headerLine = columns.map(c => sanitizeCsvCell(c.header)).join(',');
  const dataLines = rows.map(row =>
    columns.map(c => sanitizeCsvCell(c.getValue(row) ?? '')).join(',')
  );
  return [headerLine, ...dataLines].join('\r\n');
};
